import {
    INVOICE_CLASSIFICATION_MODEL,
    INVOICE_DATA_EXTRACTION_MODEL,
    INVOICE_DATA_EXTRACTION_PROMPT,
    INVOICE_DATA_EXTRACTION_JSON_SCHEMA,
    INVOICE_REASON_PROMPT,
    INVOICE_IMAGE_TEXT_EXTRACTION_MODEL,
    INVOICE_IMAGE_TEXT_EXTRACTION_PROMPT,
} from './constants'
import type {
    InvoiceDataExtractionResult,
    ParsedInvoiceMetadata,
    RawInvoiceMetadata,
} from './types'
import { retryWithValidation } from '~common/utils/retry-with-validation'
import { blobToBase64 } from '~common/utils/blob-utils'
import { postProcessLLMInvoiceDataExtraction } from './post-processing'
import type {
    LLMRequestContent,
    LLMRequestMessage,
    LLMPlugin,
    LLMResponseJSONSchema,
} from '~common/llms/types'

export interface InvoiceExtractionDeps {
    getReply: <T = unknown>(params: {
        model: string
        messages: LLMRequestMessage<string | LLMRequestContent[]>[]
        jsonResponseSchema?: LLMResponseJSONSchema
        plugins?: LLMPlugin[]
    }) => Promise<T>
}

export interface ExtractFromFileParams {
    dataUrl?: string
    base64?: string
    mimeType?: string
    fileName?: string
}

export class InvoiceExtractionService {
    constructor(private readonly deps: InvoiceExtractionDeps) {}

    async extractRawFromText(
        pdfText: string,
    ): Promise<RawInvoiceMetadata | undefined> {
        const result = await this.extractInvoiceDataAndReason({
            text: pdfText,
        })
        if (!result) {
            return undefined
        }
        const [raw] = result
        return raw
    }

    async extractRawFromImage(
        imageDataUrl: string,
    ): Promise<RawInvoiceMetadata | undefined> {
        const result = await this.extractInvoiceDataAndReason({
            image_data_url: imageDataUrl,
        })
        if (!result) {
            return undefined
        }
        const [raw] = result
        return raw
    }

    async extractFromText(pdfText: string): Promise<ParsedInvoiceMetadata> {
        const result = await this.extractInvoiceDataAndReason({ text: pdfText })
        if (!result) {
            throw new Error('Failed to extract invoice data')
        }
        const [raw] = result
        const processed = postProcessLLMInvoiceDataExtraction(pdfText, raw)
        if (!processed) {
            throw new Error('Failed to process invoice data')
        }
        return processed
    }

    async extractFromFile(
        file: ExtractFromFileParams,
    ): Promise<ParsedInvoiceMetadata> {
        const dataUrl =
            file.dataUrl ?? this.buildDataUrl(file.base64, 'application/pdf')
        if (!dataUrl) {
            throw new Error(
                'Missing file content: provide dataUrl or base64+mimeType',
            )
        }

        const text = await this.extractPdfTextFromDataUrl(dataUrl)
        return this.extractFromText(text)
    }

    private buildDataUrl(base64?: string, mimeType?: string): string | null {
        if (!base64 || !mimeType) return null
        return `data:${mimeType};base64,${base64}`
    }

    private async extractInvoiceDataAndReason(
        input: { text: string } | { image_data_url: string },
    ): Promise<[RawInvoiceMetadata, string] | undefined> {
        const isTextInput = 'text' in input

        let dataResult: InvoiceDataExtractionResult | null = null

        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                const result =
                    await this.deps.getReply<InvoiceDataExtractionResult>({
                        model: INVOICE_DATA_EXTRACTION_MODEL,
                        jsonResponseSchema: INVOICE_DATA_EXTRACTION_JSON_SCHEMA,
                        messages: isTextInput
                            ? [
                                  {
                                      role: 'user',
                                      content: INVOICE_DATA_EXTRACTION_PROMPT,
                                  },
                                  { role: 'user', content: input.text },
                              ]
                            : [
                                  {
                                      role: 'user',
                                      content: INVOICE_DATA_EXTRACTION_PROMPT,
                                  },
                                  {
                                      role: 'user',
                                      content: [
                                          {
                                              type: 'image_url',
                                              image_url: {
                                                  url: input.image_data_url,
                                              },
                                          },
                                      ],
                                  },
                              ],
                    })

                try {
                    if (this.validateInvoiceDataResponse(result)) {
                        dataResult = result
                        break
                    }
                } catch {
                    dataResult = null
                    break
                }
            } catch (error) {
                if (attempt === 2) {
                    dataResult = null
                    break
                }
            }
        }

        const dataExtractionPromise = Promise.resolve(
            dataResult as InvoiceDataExtractionResult | null,
        )

        const reasonPromise = retryWithValidation({
            operation: () =>
                this.deps.getReply<string>({
                    model: INVOICE_CLASSIFICATION_MODEL,
                    messages: isTextInput
                        ? [
                              { role: 'user', content: INVOICE_REASON_PROMPT },
                              { role: 'user', content: input.text },
                          ]
                        : [
                              { role: 'user', content: INVOICE_REASON_PROMPT },
                              {
                                  role: 'user',
                                  content: [
                                      {
                                          type: 'image_url',
                                          image_url: {
                                              url: input.image_data_url,
                                          },
                                      },
                                  ],
                              },
                          ],
                }),
            validator: (result) => typeof result === 'string',
            maxRetries: 2,
            onFailure: (err) => {
                throw err
            },
        })

        const [extractedData, reason] = await Promise.all([
            dataExtractionPromise,
            reasonPromise,
        ])

        if (
            !extractedData ||
            !extractedData.date ||
            extractedData.amount == null
        ) {
            return
        }

        return [
            {
                ...extractedData,
                date: extractedData.date,
                amount: extractedData.amount,
                reason,
            },
            reason,
        ]
    }

    private async extractPdfTextFromDataUrl(dataUrl: string): Promise<string> {
        const mimeMatch = dataUrl.match(/^data:([^;]+);base64,/)
        const mime = mimeMatch ? mimeMatch[1] : 'application/pdf'
        const isImage = mime.startsWith('image/')
        const content: LLMRequestContent[] = isImage
            ? [
                  {
                      type: 'text',
                      text: 'Extract the text from this invoice image. Return "null" if you cannot find any text.',
                  },
                  { type: 'image_url', image_url: { url: dataUrl } as any },
              ]
            : [
                  {
                      type: 'text',
                      text: 'Extract the text from this invoice PDF. Return "null" if you cannot find any text.',
                  },
                  {
                      type: 'file',
                      file: {
                          file_data: dataUrl,
                          file_name: 'invoice.pdf',
                          file_id: `file_${Date.now()}_${Math.random().toString(36).substring(2)}`,
                      },
                  },
              ]

        return this.deps.getReply<string>({
            model: INVOICE_IMAGE_TEXT_EXTRACTION_MODEL,
            messages: [
                {
                    role: 'system',
                    content: INVOICE_IMAGE_TEXT_EXTRACTION_PROMPT,
                },
                { role: 'user', content },
            ],
            plugins: isImage
                ? undefined
                : ([
                      { id: 'file-parser', pdf: { engine: 'native' } },
                  ] as unknown as any),
        })
    }

    async extractPdfTextFromImage(pdfFile: File | Blob): Promise<string> {
        let mimeType = pdfFile.type
        const isImage = mimeType.startsWith('image/')
        const isPdf =
            mimeType === 'application/pdf' ||
            mimeType === 'application/octet-stream' ||
            mimeType.includes('pdf')

        let name = isPdf ? 'invoice.pdf' : 'invoice.jpg'
        if (pdfFile instanceof File) {
            name = pdfFile.name
        }

        if (isPdf) {
            mimeType = 'application/pdf'
        }

        const base64Data = await blobToBase64(pdfFile)
        const dataUrl = `data:${mimeType};base64,${base64Data}`

        const content: LLMRequestContent[] = isImage
            ? [
                  {
                      type: 'text',
                      text: 'Extract the text from this invoice image. Return "null" if you cannot find any text.',
                  },
                  {
                      type: 'image_url',
                      image_url: {
                          url: dataUrl,
                      },
                  },
              ]
            : [
                  {
                      type: 'text',
                      text: 'Extract the text from this invoice PDF. Return "null" if you cannot find any text.',
                  },
                  {
                      type: 'file',
                      file: {
                          file_data: dataUrl,
                          file_name: name,
                          file_id: `file_${Date.now()}_${Math.random().toString(36).substring(2)}`,
                      },
                  },
              ]

        return this.deps.getReply<string>({
            model: INVOICE_IMAGE_TEXT_EXTRACTION_MODEL,
            messages: [
                {
                    role: 'system',
                    content: INVOICE_IMAGE_TEXT_EXTRACTION_PROMPT,
                },
                {
                    role: 'user',
                    content,
                },
            ],
            plugins: isImage
                ? undefined
                : ([
                      {
                          id: 'file-parser',
                          pdf: {
                              engine: 'native',
                          },
                      },
                  ] as LLMPlugin[]),
        })
    }

    private validateInvoiceDataResponse(
        response: unknown,
    ): response is InvoiceDataExtractionResult {
        if (
            !response ||
            typeof response !== 'object' ||
            Array.isArray(response)
        ) {
            return false
        }

        const requiredFields = [
            'vendorName',
            'vendorEmailAddresses',
            'vendorIbans',
            'vendorAddress',
            'vendorCountry',
            'invoiceId',
            'creditCard',
            'date',
            'amount',
            'currency',
            'vatRate',
            'vatAmount',
        ]

        const obj = response as Record<string, unknown>
        for (const field of requiredFields) {
            if (!(field in obj)) {
                return false
            }
        }

        return true
    }
}
